import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import { BusinessUnitSummary } from 'types/store/normalized/businessUnit';
import { SLICE_STATUS } from 'utils/constants';
import { getTeams, GetTeamsResponse } from 'api/teams';
import { Status } from 'utils/customTypes';

const adapter = createEntityAdapter<BusinessUnitSummary>();

interface BusinessTeamsState {
  status?: Status;
  filter?: {
    ids?: string[];
    businessUnitName?: string;
  };
  links?: {
    next?: string;
  };
  page?: {
    limit: number;
    cursor?: string;
  };
  selectedBusinessTeamIds?: string[];
}

const initialState: BusinessTeamsState = {};

/* ============================== REDUX THUNK =============================== */

const concurrencyCondition = (
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean => {
  const { status } = getBusinessTeamsState(getState());
  return status !== SLICE_STATUS.LOADING;
};

export const getBusinessTeamsList = createAsyncThunk<
  GetTeamsResponse,
  void,
  ThunkApi
>(
  'projectsList/filters/businessTeams/getBusinessTeamsList',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const { links, page = { limit: 20 } } =
      state.projectsList.filters.businessTeams;
    let url = '/v2/teams/business';
    if (links?.next) {
      url = links.next;
    } else {
      url += `?limit=${page.limit}`;
    }
    const response = await getTeams({ url });
    return response;
  },
  { condition: concurrencyCondition }
);

export const getSelectedBusinessTeamsList = createAsyncThunk<
  { data: BusinessUnitSummary[]; selected: string[] },
  { selectedBusinessTeamIds: string[] },
  ThunkApi
>(
  'projectsList/filters/businessTeams/getSelectedBusinessTeamsList',
  async ({ selectedBusinessTeamIds }, { getState }) => {
    const state = getState() as RootState;
    const allBusinessTeamIds = state.projectsList.filters.businessTeams
      .ids as string[];
    const businessTeamIdsToFetch = selectedBusinessTeamIds.filter(
      (id) => !allBusinessTeamIds.includes(id)
    );
    let url = '/v2/teams/business';
    if (businessTeamIdsToFetch.length > 0) {
      const searchParams = new URLSearchParams();
      businessTeamIdsToFetch.forEach((id) => {
        searchParams.append('filter[ids][]', id);
      });
      url += `?${searchParams.toString()}`;
    }
    const response = await getTeams({ url });
    return {
      data: response.teams,
      selected: selectedBusinessTeamIds,
    };
  }
);

const businessTeamsSlice = createSlice({
  name: 'projectsList/filters/businessTeams',
  initialState: adapter.getInitialState(initialState),
  reducers: {
    setSelectedBusinessTeamIds: (state, action) => {
      state.selectedBusinessTeamIds = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBusinessTeamsList.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.teams);
        state.links = action.payload.links;
        state.status = SLICE_STATUS.SUCCESS;
      })
      .addCase(getBusinessTeamsList.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getBusinessTeamsList.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(getSelectedBusinessTeamsList.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.data);
        state.selectedBusinessTeamIds = action.payload.selected;
      });
  },
});

export const { setSelectedBusinessTeamIds } = businessTeamsSlice.actions;

const getBusinessTeamsState = (state: RootState) =>
  state.projectsList.filters.businessTeams;

export const { selectAll: selectAllBusinessTeams } =
  adapter.getSelectors<RootState>(getBusinessTeamsState);

export const selectCanFetchMoreBusinessTeams = (state: RootState) => {
  return getBusinessTeamsState(state).links?.next ? true : false;
};

export const selectSelectedBusinessTeamIds = (state: RootState) =>
  getBusinessTeamsState(state).selectedBusinessTeamIds || [];

export const selectSelectedBusinessTeams = createSelector(
  [selectSelectedBusinessTeamIds, selectAllBusinessTeams],
  (
    selectedBusinessTeamIds: string[],
    allBusinessTeams: BusinessUnitSummary[]
  ) => {
    return allBusinessTeams.filter((team) =>
      selectedBusinessTeamIds.includes(team.id)
    );
  }
);

export default businessTeamsSlice.reducer;
